import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Pencil, Plus, RefreshCcw, X } from "lucide-react";
import { toast } from "react-toastify";
import { orderPricingApi } from "../../api/orderPricingApi";
import { useAuth } from "../../app/providers/AuthProvider";
import { FK } from "../../capabilities/featureKeys";
import { commerceSettingsStyles as styles } from "./commerceSettingsStyles";

const emptyForm = kind => ({
  kind,
  label: "",
  calculationType: "fixed",
  configuredValue: "",
  applicability: "all",
  isEnabled: true,
});

function readError(error, fallback) {
  return error?.response?.data?.message || error?.message || fallback;
}

const kindLabel = kind =>
  kind === "tax" ? "Tax" : kind === "discount" ? "Discount" : "Charge";

function formatValue(rule) {
  return rule.calculationType === "percentage"
    ? `${Number(rule.configuredValue)}%`
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
      }).format(Number(rule.configuredValue));
}

function RuleEditor({ rule, kind, saving, onClose, onSave }) {
  const [form, setForm] = useState(() =>
    rule
      ? {
          label: rule.label,
          calculationType: rule.calculationType,
          configuredValue: String(rule.configuredValue),
          applicability: rule.applicability,
          isEnabled: rule.isEnabled,
        }
      : emptyForm(kind),
  );
  const value = Number(form.configuredValue);
  const invalid =
    !form.label.trim() ||
    form.configuredValue === "" ||
    !Number.isFinite(value) ||
    value < 0 ||
    (form.calculationType === "percentage" && value > 100);

  const set = (key, next) => setForm(current => ({ ...current, [key]: next }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-editor-title"
        className={styles.dialog}
      >
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 id="pricing-editor-title" className="text-base font-semibold text-slate-900">
              {rule ? `Edit ${rule.label}` : `Add ${kindLabel(kind)}`}
            </h2>
            {rule ? <p className="mt-1 text-xs text-slate-500">Code: {rule.code}</p> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className={styles.iconButton}>
            <X size={18} />
          </button>
        </header>

        <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
          <label className="sm:col-span-2 text-sm font-medium text-slate-800">
            Label
            <input
              value={form.label}
              onChange={event => set("label", event.target.value)}
              maxLength={200}
              className={styles.field}
            />
          </label>
          <label className="text-sm font-medium text-slate-800">
            Type
            <select
              value={form.calculationType}
              onChange={event => set("calculationType", event.target.value)}
              className={styles.field}
            >
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-800">
            {form.calculationType === "percentage" ? "Percentage" : "Amount (INR)"}
            <input
              type="number"
              min="0"
              max={form.calculationType === "percentage" ? "100" : undefined}
              step="0.01"
              value={form.configuredValue}
              onChange={event => set("configuredValue", event.target.value)}
              className={styles.field}
            />
          </label>
          <label className="sm:col-span-2 text-sm font-medium text-slate-800">
            Applies to
            <select
              value={form.applicability}
              onChange={event => set("applicability", event.target.value)}
              className={styles.field}
            >
              <option value="all">All orders</option>
              <option value="delivery">Delivery</option>
              <option value="pickup">Pickup</option>
            </select>
          </label>
          {rule ? (
            <label className="sm:col-span-2 flex items-center justify-between rounded-md border border-slate-200 px-3 py-3 text-sm font-medium text-slate-800">
              Enabled
              <input
                type="checkbox"
                checked={form.isEnabled}
                onChange={event => set("isEnabled", event.target.checked)}
                className="h-5 w-5 accent-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
              />
            </label>
          ) : null}
        </div>

        <footer className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button type="button" onClick={onClose} className={styles.secondaryButton}>Cancel</button>
          <button
            type="button"
            disabled={invalid || saving}
            onClick={() => onSave({ ...form, configuredValue: value })}
            className={styles.primaryButton}
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : null}
            Save
          </button>
        </footer>
      </section>
    </div>
  );
}

function RuleSection({ title, kind, rules, canUpdate, readOnly = false, onAdd, onEdit }) {
  return (
    <section className={styles.card}>
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
          {readOnly ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Historical · Read-only</span> : null}
        </div>
        {canUpdate ? (
          <button type="button" onClick={() => onAdd(kind)} className={styles.primaryButton}>
            <Plus size={16} /> Add {kindLabel(kind)}
          </button>
        ) : null}
      </header>
      {rules.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-500">No {title.toLowerCase()} configured.</p>
      ) : (
        <div className="divide-y divide-slate-200">
          {rules.map(rule => (
            <div key={rule.code} className="flex items-center justify-between gap-4 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="font-medium text-slate-900">{rule.label}</p>
                  <span className={`text-xs font-medium ${rule.isEnabled ? "text-emerald-700" : "text-slate-500"}`}>
                    {rule.isEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className="mt-1 text-sm text-slate-600">
                  {rule.calculationType === "percentage" ? "Percentage" : "Fixed"} | {rule.applicability === "all" ? "All orders" : rule.applicability === "delivery" ? "Delivery" : "Pickup"}
                </p>
                <p className="mt-1 text-xs text-slate-400">{rule.code}</p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <span className="font-semibold text-slate-900">{formatValue(rule)}</span>
                {canUpdate ? (
                  <button type="button" onClick={() => onEdit(rule)} aria-label={`Edit ${rule.label}`} className={styles.iconButton}>
                    <Pencil size={17} />
                  </button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function OrderPricingEditor({ profileId = null }) {
  const { can } = useAuth();
  const canUpdate = can(FK.COMMERCE_ORDER_PRICING_UPDATE);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRules((await (profileId
        ? orderPricingApi.list(profileId)
        : orderPricingApi.list())) || []);
    } catch (requestError) {
      setError(readError(requestError, "Taxes and charges could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => ({
    tax: rules.filter(rule => rule.kind === "tax"),
    charge: rules.filter(rule => rule.kind === "charge"),
    discount: rules.filter(rule => rule.kind === "discount"),
  }), [rules]);

  async function save(form) {
    if (saving) return;
    setSaving(true);
    try {
      if (editor.rule) {
        const request = {
          expectedRevisionNumber: editor.rule.revisionNumber,
          label: form.label.trim(),
          calculationType: form.calculationType,
          configuredValue: form.configuredValue,
          applicability: form.applicability,
          sortOrder: editor.rule.sortOrder,
          isEnabled: form.isEnabled,
        };
        await (profileId
          ? orderPricingApi.update(editor.rule.code, request, profileId)
          : orderPricingApi.update(editor.rule.code, request));
      } else {
        const request = {
          label: form.label.trim(),
          kind: editor.kind,
          calculationType: form.calculationType,
          configuredValue: form.configuredValue,
          applicability: form.applicability,
          sortOrder: 0,
        };
        await (profileId
          ? orderPricingApi.create(request, profileId)
          : orderPricingApi.create(request));
      }
      setEditor(null);
      await load();
      toast.success("Tax or charge rule saved.");
    } catch (requestError) {
      toast.error(readError(requestError, "Tax or charge rule could not be saved."));
      if (requestError?.response?.status === 409) await load();
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {loading ? (
        <div role="status" className="flex min-h-48 items-center justify-center text-slate-500"><Loader2 className="mr-2 animate-spin" size={18} /> Loading taxes and charges</div>
      ) : error ? (
        <div role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          <p>{error}</p>
          <button type="button" onClick={load} className={`${styles.secondaryButton} mt-3`}><RefreshCcw size={15} /> Retry</button>
        </div>
      ) : (
        <div className="grid gap-5">
          <p className="text-sm leading-6 text-slate-600">Configure taxes and additional charges for orders using this policy. Item and service prices are managed separately in Catalogues.</p>
          <RuleSection title="Taxes" kind="tax" rules={grouped.tax} canUpdate={canUpdate} onAdd={kind => setEditor({ kind })} onEdit={rule => setEditor({ rule, kind: rule.kind })} />
          <RuleSection title="Additional charges" kind="charge" rules={grouped.charge} canUpdate={canUpdate} onAdd={kind => setEditor({ kind })} onEdit={rule => setEditor({ rule, kind: rule.kind })} />
          {grouped.discount.length > 0 ? (
            <RuleSection title="Discounts" kind="discount" rules={grouped.discount} canUpdate={false} readOnly onAdd={() => {}} onEdit={() => {}} />
          ) : null}
        </div>
      )}

      {editor ? <RuleEditor key={editor.rule?.code || editor.kind} rule={editor.rule} kind={editor.kind} saving={saving} onClose={() => setEditor(null)} onSave={save} /> : null}
    </>
  );
}
